import React from 'react';

export type InfoTableColumn = {
  key: string;
  label: string;
};

export type InfoTableRow = {
  [key: string]: string;
};

export enum InfoTableVariant {
  Dark = 'dark',
  Purple = 'purple',
  Blue = 'blue',
  Light = 'light',
}

type VariantStyles = {
  border: string;
  stripe1: string;
  stripe2: string;
  headerBorder: string;
  cardBorder: string;
  cardBg: string;
  labelText: string;
  primaryText: string;
  secondaryText: string;
  accentText: string;
  dot: string;
};

const VARIANT_STYLES: Record<InfoTableVariant, VariantStyles> = {
  [InfoTableVariant.Dark]: {
    border: 'border-pureWhite/10',
    stripe1: 'bg-pureWhite/5',
    stripe2: 'bg-pureWhite/[0.02]',
    headerBorder: 'border-pureWhite/10',
    cardBorder: 'border-pureWhite/10',
    cardBg: 'bg-pureWhite/5',
    labelText: 'text-pureWhite/50',
    primaryText: 'text-pureWhite',
    secondaryText: 'text-pureWhite/70',
    accentText: 'text-lightPurple',
    dot: 'bg-lightPurple',
  },
  [InfoTableVariant.Purple]: {
    border: 'border-purple/30',
    stripe1: 'bg-purple/10',
    stripe2: 'bg-purple/5',
    headerBorder: 'border-purple/30',
    cardBorder: 'border-purple/30',
    cardBg: 'bg-purple/10',
    labelText: 'text-pureWhite/50',
    primaryText: 'text-pureWhite',
    secondaryText: 'text-pureWhite/70',
    accentText: 'text-lightPurple',
    dot: 'bg-lightPurple',
  },
  [InfoTableVariant.Blue]: {
    border: 'border-blueElectric/30',
    stripe1: 'bg-blueElectric/10',
    stripe2: 'bg-blueElectric/5',
    headerBorder: 'border-blueElectric/30',
    cardBorder: 'border-blueElectric/30',
    cardBg: 'bg-blueElectric/10',
    labelText: 'text-pureWhite/50',
    primaryText: 'text-pureWhite',
    secondaryText: 'text-pureWhite/70',
    accentText: 'text-lightPurple',
    dot: 'bg-lightPurple',
  },
  [InfoTableVariant.Light]: {
    border: 'border-black/10',
    stripe1: 'bg-black/5',
    stripe2: 'bg-black/[0.02]',
    headerBorder: 'border-black/10',
    cardBorder: 'border-black/10',
    cardBg: 'bg-black/5',
    labelText: 'text-black/50',
    primaryText: 'text-black',
    secondaryText: 'text-black/70',
    accentText: 'text-purple',
    dot: 'bg-purple',
  },
};

type InfoTableProps = {
  columns: InfoTableColumn[];
  rows: InfoTableRow[];
  variant?: InfoTableVariant;
  description?: string;
  footer?: string;
};

const InfoTable: React.FC<InfoTableProps> = ({ columns, rows, variant = InfoTableVariant.Dark, description, footer }) => {
  const s = VARIANT_STYLES[variant];
  const lastColKey = columns[columns.length - 1].key;

  return (
    <>
      {description && <p className={`max-w-3xl mb-8 leading-relaxed ${s.secondaryText}`}>{description}</p>}
      {/* Desktop table */}
      <div className={`hidden md:block overflow-x-auto rounded-2xl border ${s.border}`}>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className={`border-b ${s.headerBorder}`}>
              {columns.map((col) => (
                <th key={col.key} className={`py-3 px-5 text-xs font-semibold uppercase tracking-widest ${s.labelText}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={`border-b last:border-0 ${s.headerBorder} ${i % 2 === 0 ? s.stripe1 : s.stripe2}`}>
                {columns.map((col, j) => {
                  const isFirst = j === 0;
                  const isLast = col.key === lastColKey;
                  return (
                    <td
                      key={col.key}
                      className={`py-4 px-5 align-top leading-relaxed ${
                        isFirst ? `font-semibold ${s.primaryText} whitespace-nowrap w-px` : isLast ? '${s.accentText} font-medium whitespace-nowrap' : s.secondaryText
                      }`}
                    >
                      {isFirst ? (
                        <span className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full ${s.dot} shrink-0`} />
                          {row[col.key]}
                        </span>
                      ) : (
                        row[col.key]
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="flex flex-col gap-4 md:hidden">
        {rows.map((row, i) => (
          <div key={i} className={`border ${s.cardBorder} rounded-2xl p-4 ${s.cardBg} flex flex-col gap-3`}>
            {columns.map((col, j) => {
              const isFirst = j === 0;
              const isLast = col.key === lastColKey;
              return (
                <div key={col.key}>
                  <p className={`text-xs font-semibold uppercase tracking-widest ${s.labelText} mb-1`}>{col.label}</p>
                  <p className={`leading-relaxed ${isFirst ? `font-semibold ${s.primaryText}` : isLast ? `${s.accentText} font-medium` : s.secondaryText}`}>
                    {row[col.key]}
                  </p>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {footer && <p className={`max-w-3xl mt-8 leading-relaxed ${s.secondaryText}`}>{footer}</p>}
    </>
  );
};

InfoTable.displayName = 'InfoTable';
export default InfoTable;
